import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Badge, entityStateBadgeVariant } from '@/components/ui/Badge';
import { adsApi } from '@/api/client';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import {
  formatNegativeMatchType,
  negativeKeywordStateLabel,
  parseNegativeKeywordsPayload,
} from '@/api/negativeKeywords';
import { parseProductMetadataPayload } from '@/utils/spCampaignCreateDraft';
import { formatTargetExpression } from '@/api/spTargets';
import { getDefaultDashboardDateRange } from '@/api/reportDateRanges';
import {
  formatCurrencyValue,
  formatNumberValue,
  formatPercentValue,
} from '@/api/keywordMetrics';
import {
  enrichSearchTermRows,
  parseSearchTermRowsPayload,
} from '@/utils/workspaceSearchTermMetrics';
import { TableSearchInput } from '@/components/TableSearchInput';
import { EditableTargetBidCell } from '@/components/EditableTargetBidCell';
import { resolveEditableSbTargetBid } from '@/api/targetBidUpdate';
import { CampaignKeywordsSection, formatDateRangeLabel } from '@/components/CampaignKeywordsSection';
import { CampaignProductTargetsSection } from '@/components/CampaignProductTargetsSection';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import { campaignAdProductLabel } from '@/types/spCampaigns';

export const AD_GROUP_SECTIONS = [
  { id: 'ads', label: 'Ads' },
  { id: 'product-targeting', label: 'Product targeting' },
  { id: 'keyword-targeting', label: 'Keyword targeting' },
  { id: 'negative-targeting', label: 'Negative targeting' },
  { id: 'search-terms', label: 'Search terms' },
  { id: 'creatives', label: 'Creatives' },
] as const;

export type AdGroupSectionId = (typeof AD_GROUP_SECTIONS)[number]['id'];

// SB has no ads list endpoint; SD has no keywords, negative keywords or search term report.
// Creatives are only synced for SD.
const AD_GROUP_SECTION_IDS_BY_PRODUCT: Record<CampaignAdProduct, readonly AdGroupSectionId[]> = {
  SP: ['ads', 'product-targeting', 'keyword-targeting', 'negative-targeting', 'search-terms'],
  SB: ['product-targeting', 'keyword-targeting', 'negative-targeting', 'search-terms'],
  SD: ['ads', 'product-targeting', 'creatives'],
};

export function adGroupSectionsFor(adProduct: CampaignAdProduct) {
  const ids = AD_GROUP_SECTION_IDS_BY_PRODUCT[adProduct];
  return AD_GROUP_SECTIONS.filter((item) => ids.includes(item.id));
}

/** The first section is the default, so it is the one kept out of the URL. */
export function defaultAdGroupSection(adProduct: CampaignAdProduct): AdGroupSectionId {
  return AD_GROUP_SECTION_IDS_BY_PRODUCT[adProduct][0];
}

export function parseAdGroupSection(
  raw: string | null,
  adProduct: CampaignAdProduct
): AdGroupSectionId {
  const ids = AD_GROUP_SECTION_IDS_BY_PRODUCT[adProduct];
  // Legacy `targeting` links predate the product/keyword split.
  const normalized = raw === 'targeting' ? 'product-targeting' : raw;
  const match = ids.find((id) => id === normalized);
  return match ?? defaultAdGroupSection(adProduct);
}

type SpProductAdRow = {
  ad_id?: string;
  ad_group_id?: string;
  asin?: string;
  sku?: string;
  state?: string;
  extended_data?: {
    serving_status?: string;
  } | null;
};

function parseSpProductAdsPayload(data: unknown): {
  productAds: SpProductAdRow[];
  totalResults: number;
} {
  if (!data || typeof data !== 'object') {
    return { productAds: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const productAds = Array.isArray(raw.product_ads) ? (raw.product_ads as SpProductAdRow[]) : [];
  const totalResults =
    typeof raw.total_results === 'number' ? raw.total_results : productAds.length;
  return { productAds, totalResults };
}

type SdProductAd = { adId?: string; adGroupId?: string; asin?: string; sku?: string; state?: string };

/** SD product ads are stored Amazon payloads (camelCase) for the whole campaign. */
function parseSdProductAdsPayload(
  data: unknown,
  adGroupId: string
): { productAds: SpProductAdRow[]; totalResults: number } {
  const raw = (data ?? {}) as { product_ads?: SdProductAd[] };
  const productAds = (raw.product_ads ?? [])
    .filter((ad) => String(ad.adGroupId) === adGroupId)
    .map((ad) => ({ ad_id: ad.adId, asin: ad.asin, sku: ad.sku, state: ad.state }));
  return { productAds, totalResults: productAds.length };
}

type AdGroupTargetRow = {
  target_id?: string;
  campaign_id?: string;
  ad_group_id?: string;
  state?: string;
  bid?: number | null;
  expression?: unknown;
  resolved_expression?: unknown;
};

function targetExpressionLabel(row: AdGroupTargetRow): string {
  const resolved = formatTargetExpression(row.resolved_expression);
  return resolved !== '-' ? resolved : formatTargetExpression(row.expression);
}

const EMPTY_KEYWORD_LOOKUP = {
  textByKeywordId: new Map<string, string>(),
  matchTypeByKeywordId: new Map<string, string>(),
};

type ScopeProps = {
  profileId: string;
  campaignId: string;
  adGroupId: string;
  adProduct: CampaignAdProduct;
};

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-foreground-subtle">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  );
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="text-sm text-error">
      {message}
      <button type="button" onClick={onRetry} className="ml-2 text-brand underline">
        Retry
      </button>
    </div>
  );
}

function AdGroupAdsSection({ profileId, campaignId, adGroupId, adProduct }: ScopeProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey:
      adProduct === 'SD'
        ? ['sd-product-ads', profileId, campaignId]
        : ['sp-product-ads', profileId, campaignId, adGroupId],
    queryFn: async () => {
      if (adProduct === 'SD') {
        // No ad-group filter on this endpoint; parseSdProductAdsPayload narrows it.
        const res = await adsApi.getSdProductAds(profileId, null, { campaignIds: [campaignId] });
        if (!res.success) throw new Error('Failed to load ads');
        return res.data;
      }
      const res = await adsApi.listSpProductAds(profileId, {
        include_extended_data_fields: true,
        campaign_ids: [campaignId],
        ad_group_ids: [adGroupId],
        ad_ids: [],
        states: ['ENABLED', 'PAUSED', 'ARCHIVED'],
        max_results: 500,
      });
      if (!res.success) throw new Error('Failed to load ads');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!adGroupId,
  });

  const { productAds, totalResults } = useMemo(
    () =>
      adProduct === 'SD'
        ? parseSdProductAdsPayload(data, adGroupId)
        : parseSpProductAdsPayload(data),
    [data, adProduct, adGroupId]
  );

  const uniqueAsins = useMemo(() => {
    const seen = new Set<string>();
    for (const ad of productAds) {
      const asin = ad.asin?.trim();
      if (asin) seen.add(asin);
    }
    return [...seen];
  }, [productAds]);

  const { data: productMetadata } = useQuery({
    queryKey: ['campaign-details-product-metadata', profileId, adProduct, uniqueAsins],
    queryFn: async () => {
      const res = await adsApi.getProductMetadata(profileId, {
        adType: adProduct,
        asins: uniqueAsins,
        pageSize: Math.min(100, Math.max(25, uniqueAsins.length)),
        pageIndex: 0,
      });
      if (!res.success) throw new Error('Failed to load product metadata');
      return parseProductMetadataPayload(res.data);
    },
    enabled: !!profileId && uniqueAsins.length > 0,
  });

  const metadataByAsin = useMemo(() => {
    const map = new Map<string, { title?: string; imageUrl?: string }>();
    for (const product of productMetadata ?? []) {
      map.set(product.asin.toUpperCase(), { title: product.title, imageUrl: product.imageUrl });
    }
    return map;
  }, [productMetadata]);

  const visibleAds = useMemo(() => {
    return filterRowsByTableSearch(productAds, searchQuery, (ad) => {
      const asin = ad.asin || '';
      const meta = asin ? metadataByAsin.get(asin.toUpperCase()) : undefined;
      return [meta?.title, ad.asin, ad.sku, ad.ad_id, ad.state, ad.extended_data?.serving_status];
    });
  }, [productAds, searchQuery, metadataByAsin]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold text-foreground">Ads</h2>
        {!isLoading && !error && productAds.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <TableSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search ads…"
              id="campaign-ad-group-ads-search"
            />
            <span className="text-sm text-foreground-subtle">{totalResults} total</span>
          </div>
        ) : null}
      </div>
      {isLoading ? (
        <LoadingRow label="Loading ads..." />
      ) : error ? (
        <ErrorRow
          message={(error as Error).message || 'Failed to load ads'}
          onRetry={() => void refetch()}
        />
      ) : productAds.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No ads found for this ad group.</p>
      ) : visibleAds.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleAds.length} of {productAds.length} ad(s)
            </p>
          ) : null}
          <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">ASIN</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">Ad ID</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Serving status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleAds.map((ad, index) => {
                  const asin = ad.asin || '';
                  const meta = asin ? metadataByAsin.get(asin.toUpperCase()) : undefined;
                  return (
                    <tr key={ad.ad_id || `idx-${index}`} className="bg-surface hover:bg-page/80">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3 min-w-[12rem]">
                          {meta?.imageUrl ? (
                            <img
                              src={meta.imageUrl}
                              alt=""
                              className="w-10 h-10 rounded object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-surface-muted shrink-0" />
                          )}
                          <span className="text-foreground truncate">
                            {meta?.title || asin || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">{asin || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted">{ad.sku || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">
                        {ad.ad_id || '-'}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{ad.state || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {ad.extended_data?.serving_status || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function AdGroupNegativeTargetingSection({
  profileId,
  campaignId,
  adGroupId,
  adProduct,
}: ScopeProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'campaign-details-section-negative-keywords',
      profileId,
      campaignId,
      adProduct,
      adGroupId,
    ],
    queryFn: async () => {
      if (adProduct === 'SB') {
        const res = await adsApi.listSbNegativeKeywords(profileId, {
          campaignId,
          adGroupId,
          stateFilter: 'enabled,archived',
          count: 500,
        });
        if (!res.success) throw new Error('Failed to load negative targeting');
        return res.data;
      }
      const res = await adsApi.listSpNegativeKeywords(profileId, {
        campaign_ids: [campaignId],
        ad_group_ids: [adGroupId],
        states: ['ENABLED', 'PAUSED'],
        max_results: 1000,
      });
      if (!res.success) throw new Error('Failed to load negative targeting');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!adGroupId,
  });

  const payload = useMemo(() => parseNegativeKeywordsPayload(data), [data]);

  const visibleRows = useMemo(() => {
    return filterRowsByTableSearch(payload.negative_keywords, searchQuery, (row) => [
      row.keyword_text,
      formatNegativeMatchType(row.match_type || ''),
      negativeKeywordStateLabel(row.state, adProduct),
      row.negative_keyword_id,
    ]);
  }, [payload.negative_keywords, searchQuery, adProduct]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold text-foreground">Negative targeting</h2>
        {!isLoading && !error && payload.negative_keywords.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <TableSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search negative targeting…"
              id="campaign-ad-group-negatives-search"
            />
            <span className="text-sm text-foreground-subtle">{payload.total_results} total</span>
          </div>
        ) : null}
      </div>
      {isLoading ? (
        <LoadingRow label="Loading negative targeting..." />
      ) : error ? (
        <ErrorRow
          message={(error as Error).message || 'Failed to load negative targeting'}
          onRetry={() => void refetch()}
        />
      ) : payload.negative_keywords.length === 0 ? (
        <p className="text-sm text-foreground-subtle">
          No negative targeting found for this ad group.
        </p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleRows.length} of {payload.negative_keywords.length} negative keyword(s)
            </p>
          ) : null}
          <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Keyword</th>
                  <th className="px-3 py-2">Match type</th>
                  <th className="px-3 py-2">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleRows.map((row, index) => (
                  <tr
                    key={row.negative_keyword_id || `neg-${index}`}
                    className="bg-surface hover:bg-page/80"
                  >
                    <td className="px-3 py-2 text-foreground">{row.keyword_text || '-'}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {formatNegativeMatchType(row.match_type || '')}
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {negativeKeywordStateLabel(row.state, adProduct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

type TargetStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';

/** SB/SD product targeting for one ad group (mirrors CampaignProductTargetsSection for SP). */
function AdGroupTargetsTable({
  profileId,
  campaignId,
  adGroupId,
  adProduct,
  adGroupNameById,
}: ScopeProps & { adGroupNameById?: Map<string, string> }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<TargetStateFilter>('ALL');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['campaign-ad-group-targets', adProduct, profileId],
    queryFn: async () => {
      // Neither endpoint filters by campaign, ad group or state, so scoping happens client-side.
      const res =
        adProduct === 'SB'
          ? await adsApi.listSbTargets(profileId, {})
          : await adsApi.getSdTargets(profileId);
      if (!res.success) throw new Error('Failed to load product targets');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!adGroupId,
  });

  // Neither endpoint filters by ad group; ad group IDs are unique, so that alone scopes it.
  const targets = useMemo(() => {
    const list = ((data ?? {}) as { targets?: AdGroupTargetRow[] }).targets ?? [];
    return list.filter((row) => String(row.ad_group_id) === adGroupId);
  }, [data, adGroupId]);

  const stateFilteredTargets = useMemo(() => {
    if (stateFilter === 'ALL') return targets;
    return targets.filter((row) => (row.state || '').toUpperCase() === stateFilter);
  }, [targets, stateFilter]);

  const adGroupName = adGroupNameById?.get(adGroupId) || adGroupId;

  const visibleTargets = useMemo(() => {
    return filterRowsByTableSearch(stateFilteredTargets, searchQuery, (row) => [
      targetExpressionLabel(row),
      adGroupName,
      row.bid != null ? formatCurrencyValue(Number(row.bid)) : null,
      row.state,
      row.target_id,
    ]);
  }, [stateFilteredTargets, searchQuery, adGroupName]);

  const showLoading = (isLoading || isFetching) && targets.length === 0;

  return (
    <div className="bg-surface rounded-xl shadow border border-border p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Product targeting</h2>
          {!showLoading && !error && (
            <p className="text-sm text-foreground-subtle mt-0.5">
              {stateFilteredTargets.length} total
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <TableSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search product targets…"
            id="campaign-ad-group-targets-search"
          />
          <div>
            <label className="block text-xs font-medium text-foreground-muted uppercase tracking-wide mb-1">
              State
            </label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as TargetStateFilter)}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-page"
          >
            Refresh
          </button>
        </div>
      </div>
      {showLoading ? (
        <LoadingRow label="Loading product targets..." />
      ) : error ? (
        <ErrorRow
          message={(error as Error).message || 'Failed to load product targets'}
          onRetry={() => void refetch()}
        />
      ) : stateFilteredTargets.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No targets found for this ad group.</p>
      ) : visibleTargets.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleTargets.length} of {stateFilteredTargets.length} product target(s)
            </p>
          ) : null}
          <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Ad group</th>
                  <th className="px-3 py-2">Bid</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Target ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleTargets.map((row, index) => {
                  const bid = row.bid != null ? Number(row.bid) : undefined;
                  const bidLabel = bid != null ? formatCurrencyValue(bid) : '-';
                  const label = targetExpressionLabel(row);
                  return (
                    <tr key={row.target_id || `tgt-${index}`} className="bg-surface hover:bg-page/80">
                      <td className="px-3 py-2 text-foreground break-all">{label}</td>
                      <td className="px-3 py-2 text-foreground-muted" title={adGroupId}>
                        {adGroupName}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">
                        {/* SD has no target bid update endpoint, so only SB bids are editable. */}
                        {adProduct === 'SB' ? (
                          <EditableTargetBidCell
                            profileId={profileId}
                            displayValue={bidLabel}
                            editableTarget={resolveEditableSbTargetBid({
                              target_id: row.target_id,
                              campaign_id: row.campaign_id,
                              ad_group_id: row.ad_group_id,
                              state: row.state,
                              bid,
                            })}
                            targetLabel={label}
                            onUpdated={() => void refetch()}
                          />
                        ) : (
                          bidLabel
                        )}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{row.state || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted font-mono">
                        {row.target_id || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

type SdCreativeRow = {
  creative_id?: string;
  state?: string;
  creative_type?: string | null;
  moderation_status?: string | null;
  headline?: string | null;
};

function AdGroupCreativesSection({ profileId, campaignId, adGroupId }: ScopeProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sd-creatives', profileId, campaignId, adGroupId],
    queryFn: async () => {
      const res = await adsApi.getSdCreatives(profileId, {
        campaignIds: [campaignId],
        adGroupIds: [adGroupId],
      });
      if (!res.success) throw new Error('Failed to load creatives');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!adGroupId,
  });

  const creatives = ((data ?? {}) as { creatives?: SdCreativeRow[] }).creatives ?? [];

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-3">Creatives</h2>
      {isLoading ? (
        <LoadingRow label="Loading creatives..." />
      ) : error ? (
        <ErrorRow
          message={(error as Error).message || 'Failed to load creatives'}
          onRetry={() => void refetch()}
        />
      ) : creatives.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No creatives found for this ad group.</p>
      ) : (
        <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                <th className="px-3 py-2">Headline</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Moderation</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Creative ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {creatives.map((row, index) => (
                <tr key={row.creative_id || `cr-${index}`} className="bg-surface hover:bg-page/80">
                  <td className="px-3 py-2 text-foreground">{row.headline || '-'}</td>
                  <td className="px-3 py-2 text-foreground-muted">{row.creative_type || '-'}</td>
                  <td className="px-3 py-2 text-foreground-muted">
                    {row.moderation_status || '-'}
                  </td>
                  <td className="px-3 py-2 text-foreground-muted">{row.state || '-'}</td>
                  <td className="px-3 py-2 text-foreground-muted font-mono">
                    {row.creative_id || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const SEARCH_TERMS_LIMIT = 1000;

function AdGroupSearchTermsSection({ profileId, campaignId, adGroupId }: ScopeProps) {
  const dateRange = useMemo(() => getDefaultDashboardDateRange(), []);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'campaign-ad-group-search-terms',
      profileId,
      campaignId,
      adGroupId,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: async () => {
      const res = await adsApi.getSearchTermReportRows(profileId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        campaignIds: campaignId,
        adGroupIds: adGroupId,
        limit: SEARCH_TERMS_LIMIT,
      });
      if (!res.success) throw new Error('Failed to load search terms');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && !!adGroupId,
  });

  const payload = useMemo(() => parseSearchTermRowsPayload(data), [data]);
  const rows = useMemo(
    () =>
      enrichSearchTermRows(payload.rows, null, new Map(), new Map(), EMPTY_KEYWORD_LOOKUP, []),
    [payload.rows]
  );

  const visibleRows = useMemo(() => {
    return filterRowsByTableSearch(rows, searchQuery, (row) => [
      row.searchTerm,
      row.target,
      row.matchType,
    ]);
  }, [rows, searchQuery]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Search terms</h2>
          <p className="text-sm text-foreground-subtle mt-0.5">
            Performance: {formatDateRangeLabel(dateRange.startDate, dateRange.endDate)}
          </p>
        </div>
        {!isLoading && !error && rows.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <TableSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search search terms…"
              id="campaign-ad-group-search-terms-search"
            />
            <span className="text-sm text-foreground-subtle">{payload.totalCount} total</span>
          </div>
        ) : null}
      </div>
      {isLoading ? (
        <LoadingRow label="Loading search terms..." />
      ) : error ? (
        <ErrorRow
          message={(error as Error).message || 'Failed to load search terms'}
          onRetry={() => void refetch()}
        />
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">
          No search terms found for this ad group in this period.
        </p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleRows.length} of {rows.length} search term(s)
            </p>
          ) : payload.totalCount > rows.length ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing top {rows.length} of {payload.totalCount} search term(s) by spend
            </p>
          ) : null}
          <div className="max-h-96 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Search term</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Match</th>
                  <th className="px-3 py-2 text-right">Impressions</th>
                  <th className="px-3 py-2 text-right">Clicks</th>
                  <th className="px-3 py-2 text-right">Spend</th>
                  <th className="px-3 py-2 text-right">CPC</th>
                  <th className="px-3 py-2 text-right">CTR</th>
                  <th className="px-3 py-2 text-right">Purchases</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleRows.map((row) => (
                  <tr key={row.rowKey} className="bg-surface hover:bg-page/80">
                    <td className="px-3 py-2 text-foreground font-medium">{row.searchTerm}</td>
                    <td className="px-3 py-2 text-foreground-muted">{row.target}</td>
                    <td className="px-3 py-2 text-foreground-muted uppercase text-xs">
                      {row.matchType}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatNumberValue(row.metrics.impressions, 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatNumberValue(row.metrics.clicks, 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatCurrencyValue(row.metrics.cost)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatCurrencyValue(row.metrics.cpc)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatPercentValue(row.metrics.ctr)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatNumberValue(row.metrics.purchases30d, 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatCurrencyValue(row.metrics.sales30d)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatNumberValue(row.metrics.roas)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

type CampaignAdGroupContentProps = ScopeProps & {
  adGroupName: string;
  adGroupState?: string;
  /** Campaign budget; ad groups have no budget of their own. */
  campaignBudget?: number;
  /** Parent campaign AUTO/MANUAL label, if known. */
  campaignTargetingLabel?: string;
  section: AdGroupSectionId;
  adGroupNameById?: Map<string, string>;
};

export function CampaignAdGroupContent({
  profileId,
  campaignId,
  adGroupId,
  adProduct,
  adGroupName,
  adGroupState,
  campaignBudget,
  campaignTargetingLabel,
  section,
  adGroupNameById,
}: CampaignAdGroupContentProps) {
  const scope = { profileId, campaignId, adGroupId, adProduct };
  return (
    <div>
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold text-foreground break-all">{adGroupName}</h2>
          {adGroupState ? (
            <Badge variant={entityStateBadgeVariant(adGroupState)}>{adGroupState}</Badge>
          ) : null}
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground-muted">
          <span>Type: {campaignAdProductLabel(adProduct)}</span>
          {campaignTargetingLabel && campaignTargetingLabel !== 'N/A' ? (
            <>
              <span aria-hidden>|</span>
              <span>Targeting: {campaignTargetingLabel}</span>
            </>
          ) : null}
          {campaignBudget != null ? (
            <>
              <span aria-hidden>|</span>
              <span>Budget: {formatCurrencyValue(campaignBudget)}</span>
            </>
          ) : null}
          <span aria-hidden>|</span>
          <span className="break-all">Ad group ID: {adGroupId}</span>
        </p>
      </div>

      {section === 'ads' && <AdGroupAdsSection {...scope} />}

      {section === 'product-targeting' &&
        (adProduct === 'SP' ? (
          // Keyed by ad group so search, paging and loaded rows never carry over between ad groups.
          <CampaignProductTargetsSection
            key={adGroupId}
            profileId={profileId}
            campaignId={campaignId}
            adGroupId={adGroupId}
            adGroupNameById={adGroupNameById}
          />
        ) : (
          <AdGroupTargetsTable key={adGroupId} {...scope} adGroupNameById={adGroupNameById} />
        ))}

      {section === 'keyword-targeting' && (
        <CampaignKeywordsSection
          key={adGroupId}
          profileId={profileId}
          campaignId={campaignId}
          adProduct={adProduct}
          adGroupId={adGroupId}
          adGroupNameById={adGroupNameById}
          embedded
        />
      )}

      {section === 'negative-targeting' && <AdGroupNegativeTargetingSection {...scope} />}

      {section === 'search-terms' && <AdGroupSearchTermsSection {...scope} />}

      {section === 'creatives' && <AdGroupCreativesSection {...scope} />}
    </div>
  );
}
